import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    Grid,
    Rating,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, useRouteMatch } from 'react-router-dom';
import TitleCase from '../components/BasicComponents/TitleCase';
import Loading from '../components/Loading';
import Title from '../components/Navbar/Title';
import { fetchSchool } from '../redux/SchoolSlice';
import { grades } from '../utils/grades';
import ViewCrtProfile from './ViewCrtProfile';
import ViewSavedCrtProfile from './ViewSavedCrtProfile';
import logoAlone from '../assets/airTeachr-Logo-alone.png';

const GeneralText = styled(TextField)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '97%',
}));

const TeacherResultsContainerBox = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridGap: '20px',
    gridRowGap: 0,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 300px))',
    justifyContent: 'start',
    alignItems: 'center',
    marginTop: '30px',
}));

const TeacherResultsCard = styled(Card)(({ theme }) => ({
    width: '100%',
    margin: '20px auto',
    padding: '20px',
    boxShadow: '1px 1px 9px -2px rgb(0 0 0 / 20%)',
    borderRadius: '10px',
    '&:hover, &:focus, &:active': {
        position: 'relative',
        top: '-5px',
        cursor: 'pointer',
        border: `1px solid ${theme.palette.primary.main}`,
    },
}));

const TeacherImgContainer = styled('div')(({ theme }) => ({
    margin: 'auto',
    width: '100%',
    textAlign: 'center',
}));

const TeacherImg = styled('img')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    borderRadius: '10px',
}));

const MiniTeacherImg = styled('img')(({ theme }) => ({
    width: '60px',
    height: 'auto',
    borderRadius: '10px',
}));

const TeacherInfoType = styled(Box)(({ theme }) => ({
    padding: '10px 4px 0',
}));

const BookingButton = styled(LoadingButton)(({ theme }) => ({
    padding: '10px 0px',
    margin: '10px auto',
    width: '100%',
    maxWidth: '150px',
    borderRadius: '30px',
}));

const SavedIcon = () => {
    return (
        <Grid item align="right">
            <Grid container>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }} p={0.5}>
                    Saved
                </Typography>
                <img src={logoAlone} width="40" />
            </Grid>
        </Grid>
    );
};

const validPermission = 'update:schoolProfile';
const certifiedPermission = 'create:availability';

const SavedTeachers = props => {
    let match = useRouteMatch();
    let history = useHistory();

    const [teacherList, setTeacherList] = useState({
        count: 0,
        responseVals: [],
    });

    const [loadingCrtSearch, setloadingCrtSearch] = useState(false);
    const [resultMessage, setResultMessage] = useState({
        error: false,
        errorText: '',
    });
    const [state, setState] = useState({
        schoolAdmin: false,
        fullVerification: false,
        firstName: { value: '', touched: false, error: '', errorCode: '' },
        lastName: { value: '', touched: false, error: '', errorCode: '' },
    });

    const { getAccessTokenSilently } = useAuth0();

    const dispatch = useDispatch();
    /*******************
     * Profile selector
     *******************/
    const profile = useSelector(state => state.profile.profile);
    const profileStatus = useSelector(state => state.profile.status);
    const memToken = useSelector(state => state.token);
    const tokenStatus = useSelector(state => state.token.status);
    const schoolInfo = useSelector(state => state.school);

    /*******************
     * School selector
     *******************/
    const school = useSelector(state => state.school);
    const schoolStatus = useSelector(state => state.school.status);

    const updateField = event => {
        const { id, value } = event.target;
        var itemStruct = {
            value: value,
            touched: true,
            error: state.error,
            errorCode: state.errorCode,
        };
        setState({
            ...state,
            [id]: itemStruct,
        });
    };

    const clearSearch = () => {
        setTeacherList({
            count: 0,
            responseVals: [],
        });
        setResultMessage({ error: false, errorText: '' });
    };

    const isSaved = teacher => {
        const id = teacher.uniqueId;
        if (id === undefined || school.school.Data.crtList.length === 0)
            return false;
        for (const teacher of school.school.Data.crtList) {
            if (teacher.uniqueId === id) {
                return true;
            }
        }
        return false;
    };

    useEffect(() => {
        setTeacherList({
            ...teacherList,
            responseVals: teacherList.responseVals.map(teacher => {
                teacher.saved = isSaved(teacher);
                return teacher;
            }),
        });
    }, [school]);

    const handleCrtNameSearch = event => {
        try {
            setloadingCrtSearch(true);
            setResultMessage({ error: false, errorText: '' });
            async function crtNameSearch() {
                const token = await getAccessTokenSilently({
                    scope: process.env.REACT_APP_scope,
                });
                let response = await axios.get('/crtNameSearch', {
                    params: {
                        'lat': school.school.Data.lat,
                        'lng': school.school.Data.lng,
                        'fn': state.firstName.value,
                        'ln': state.lastName.value,
                    },
                    baseURL: process.env.REACT_APP_API_GATEWAY,
                    url: `/crtNameSearch`,
                    timeout: 30000,
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Bearer ' + token,
                    },
                });

                console.log(`response ${response.status}`);

                if (response.status === 200) {
                    for (const teacher of response.data.responseVals) {
                        teacher.saved = isSaved(teacher);
                    }
                    setTeacherList(response.data);
                    setResultMessage({
                        error: false,
                        errorText: 'Search Complete!',
                    });
                } else {
                    throw new Error('search failed');
                }
            }
            crtNameSearch().catch(err => {
                setResultMessage({
                    error: true,
                    errorText: 'Search Failed',
                });
            });
        } catch (err) {
            setResultMessage({
                error: true,
                errorText: 'Search Failed',
            });
        } finally {
            setloadingCrtSearch(false);
        }
    };

    useEffect(() => {
        if (profileStatus === 'succeeded' && profile.Data.Count > 0) {
            if (profile.Data.Items[0].type === 'School') {
                async function dispatchSchoolFromStore() {
                    const token = await getAccessTokenSilently({
                        scope: process.env.REACT_APP_scope,
                    });
                    const requestData = {
                        'token': token,
                        'schoolId': profile.Data.Items[0]['gs1-pk'],
                    };
                    await dispatch(fetchSchool(requestData));
                }
                dispatchSchoolFromStore();
            } else if (profile.Data.Items[0].type === 'Teacher') {
                //This function is initialse the data if it doesnt exist, else it well set it from the profile state
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAccessTokenSilently, profileStatus, dispatch]);

    useEffect(() => {
        if (tokenStatus === 'succeeded' && memToken.Count === 1) {
            if (memToken.permissions.includes(validPermission))
                setState({
                    ...state,
                    schoolAdmin: true,
                });
            if (memToken.permissions.includes(certifiedPermission))
                setState({
                    ...state,
                    fullVerification: true,
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenStatus, memToken.Count, memToken.permissions]);

    return (
        <>
            {schoolStatus === 'succeeded' ? (
                <>
                    <Box sx={{ width: '100%' }}>
                        {/* {typeof schoolInfo.school.Data.planStatus !==
                        'undefined' ? ( */}
                        <Box>
                            {/* {schoolInfo.school.Data.planStatus ===
                                'Active' ? ( */}
                            <>
                                <Title title="Saved Teachers" />
                                <Grid
                                    container
                                    xs={12}
                                    directon="row"
                                    justifyContent="space-evenly"
                                    alignItems="top"
                                >
                                    <Route
                                        path="/casual-relief-teacher-list/profile/:id"
                                        children={
                                            <ViewCrtProfile
                                                list={teacherList}
                                                school={school.school}
                                                schoolStatus={schoolStatus}
                                            />
                                        }
                                    ></Route>
                                    <Route
                                        path="/casual-relief-teacher-list/saved-profile/:id"
                                        children={
                                            <ViewSavedCrtProfile
                                                school={school}
                                                schoolStatus={schoolStatus}
                                            />
                                        }
                                    ></Route>
                                    <Route
                                        path="/casual-relief-teacher-list"
                                        exact
                                    >
                                        <Grid
                                            item
                                            xs={10}
                                            sx={{
                                                backgroundColor: '#FFF',
                                                borderRadius: '5px',
                                                boxShadow:
                                                    '1px 1px 9px -2px rgb(0 0 0 / 20%)',
                                                padding: '24px',
                                                marginTop: '24px',
                                            }}
                                        >
                                            <Accordion
                                                data-cy="search-card"
                                                sx={{
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={
                                                        <ExpandMoreIcon />
                                                    }
                                                    data-cy="teacher-search"
                                                >
                                                    <Typography variant="h3">
                                                        Teacher Search
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Grid
                                                        container
                                                        xs={12}
                                                        directon="row"
                                                        justifyContent="space-evenly"
                                                        alignItems="top"
                                                    >
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                        >
                                                            <GeneralText
                                                                data-cy="first-name"
                                                                id="firstName"
                                                                label="First Name"
                                                                variant="outlined"
                                                                value={
                                                                    state
                                                                        .firstName
                                                                        .value
                                                                }
                                                                onChange={
                                                                    updateField
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={6}
                                                        >
                                                            <GeneralText
                                                                data-cy="last-name"
                                                                id="lastName"
                                                                label="Last Name"
                                                                variant="outlined"
                                                                value={
                                                                    state
                                                                        .lastName
                                                                        .value
                                                                }
                                                                onChange={
                                                                    updateField
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <BookingButton
                                                                id="searchTeacher"
                                                                data-cy="search"
                                                                size="large"
                                                                color="primary"
                                                                onClick={event => {
                                                                    handleCrtNameSearch(
                                                                        event
                                                                    );
                                                                }}
                                                                // loading={loadingCrtSearch}
                                                                // loadingPosition="start"
                                                                startIcon={
                                                                    <SearchIcon />
                                                                }
                                                                variant="outlined"
                                                                loading={
                                                                    loadingCrtSearch
                                                                }
                                                                loadingPosition="start"
                                                            >
                                                                Search
                                                            </BookingButton>
                                                            {teacherList.count >
                                                                0 && (
                                                                <BookingButton
                                                                    id="clearSearch"
                                                                    size="large"
                                                                    color="primary"
                                                                    onClick={
                                                                        clearSearch
                                                                    }
                                                                    variant="outlined"
                                                                    sx={{
                                                                        marginLeft:
                                                                            '12px',
                                                                    }}
                                                                >
                                                                    Clear
                                                                    Results
                                                                </BookingButton>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                    {teacherList.count > 0 && (
                                                        <TeacherResultsContainerBox>
                                                            {teacherList.responseVals.map(
                                                                (
                                                                    item,
                                                                    index
                                                                ) => {
                                                                    if (
                                                                        typeof item.booked ===
                                                                        'undefined'
                                                                    ) {
                                                                        return (
                                                                            <TeacherResultsCard
                                                                                data-cy="teacher-card"
                                                                                onClick={() => {
                                                                                    setState(
                                                                                        {
                                                                                            ...state,
                                                                                            selectTeacher:
                                                                                                index,
                                                                                        }
                                                                                    );
                                                                                    if (
                                                                                        item.saved
                                                                                    ) {
                                                                                        history.push(
                                                                                            match.path +
                                                                                                '/saved-profile/' +
                                                                                                item.uniqueId
                                                                                        );
                                                                                    } else {
                                                                                        history.push(
                                                                                            match.path +
                                                                                                '/profile/' +
                                                                                                index
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <Grid
                                                                                    container
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        justifyContent:
                                                                                            'space-between',
                                                                                    }}
                                                                                >
                                                                                    <Grid
                                                                                        item
                                                                                    >
                                                                                        <TitleCase
                                                                                            variant="h3"
                                                                                            string={
                                                                                                item.firstName
                                                                                            }
                                                                                            data-cy="teacher-name"
                                                                                        />
                                                                                    </Grid>
                                                                                    {item.saved && (
                                                                                        <SavedIcon />
                                                                                    )}
                                                                                </Grid>

                                                                                {item.rating ? (
                                                                                    <Grid
                                                                                        container
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                7
                                                                                            }
                                                                                            sm={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <Rating
                                                                                                data-cy="rating"
                                                                                                name="simple-controlled"
                                                                                                value={
                                                                                                    item.rating
                                                                                                }
                                                                                                readOnly
                                                                                                precision={
                                                                                                    0.5
                                                                                                }
                                                                                            ></Rating>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                5
                                                                                            }
                                                                                            sm={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <Typography data-cy="num-reviews">
                                                                                                {`/ ${item.amountOfRatings} reviews`}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                ) : (
                                                                                    <Typography variant="body1">
                                                                                        No
                                                                                        Rating
                                                                                        Available
                                                                                    </Typography>
                                                                                )}

                                                                                <TeacherImgContainer>
                                                                                    <TeacherImg
                                                                                        src={
                                                                                            item.profileImage
                                                                                        }
                                                                                    />
                                                                                </TeacherImgContainer>
                                                                                <TeacherInfoType
                                                                                    variant="body1"
                                                                                    data-cy="teacher-grades"
                                                                                >
                                                                                    {item.grades.map(
                                                                                        grade => {
                                                                                            return (
                                                                                                <Typography variant="body1">
                                                                                                    {
                                                                                                        grades.find(
                                                                                                            g =>
                                                                                                                g.name ===
                                                                                                                grade
                                                                                                        )
                                                                                                            .title
                                                                                                    }
                                                                                                </Typography>
                                                                                            );
                                                                                        }
                                                                                    )}
                                                                                </TeacherInfoType>
                                                                            </TeacherResultsCard>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <div></div>
                                                                    );
                                                                }
                                                            )}
                                                        </TeacherResultsContainerBox>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                            {resultMessage.errorText !== '' && (
                                                <Grid item xs={12}>
                                                    <Alert
                                                        severity={
                                                            resultMessage.error ===
                                                            true
                                                                ? 'error'
                                                                : 'success'
                                                        }
                                                    >
                                                        {
                                                            resultMessage.errorText
                                                        }
                                                    </Alert>
                                                </Grid>
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={10}
                                            sx={{
                                                backgroundColor: '#FFF',
                                                borderRadius: '5px',
                                                boxShadow:
                                                    '1px 1px 9px -2px rgb(0 0 0 / 20%)',
                                                padding: '24px',
                                                marginTop: '24px',
                                            }}
                                        >
                                            <Typography variant="h3">
                                                Saved Teacher List
                                            </Typography>
                                            {schoolInfo.school.Data.crtList
                                                .length > 0 ? (
                                                <TeacherResultsContainerBox>
                                                    {schoolInfo.school.Data.crtList.map(
                                                        (item, index) => {
                                                            if (
                                                                typeof item.booked ===
                                                                'undefined'
                                                            ) {
                                                                return (
                                                                    <TeacherResultsCard
                                                                        data-cy="saved-teacher"
                                                                        onClick={() => {
                                                                            setState(
                                                                                {
                                                                                    ...state,
                                                                                    selectTeacher:
                                                                                        item.uniqueId,
                                                                                }
                                                                            );
                                                                            history.push(
                                                                                match.path +
                                                                                    '/saved-profile/' +
                                                                                    item.uniqueId
                                                                            );
                                                                        }}
                                                                    >
                                                                        <Grid
                                                                            container
                                                                        >
                                                                            <Grid
                                                                                item
                                                                                xs={
                                                                                    4
                                                                                }
                                                                            >
                                                                                <MiniTeacherImg
                                                                                    src={
                                                                                        item.img
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                xs={
                                                                                    8
                                                                                }
                                                                            >
                                                                                <TitleCase
                                                                                    variant="h5"
                                                                                    data-cy="first-name"
                                                                                    string={
                                                                                        item.firstName
                                                                                    }
                                                                                ></TitleCase>
                                                                                <TitleCase
                                                                                    variant="h5"
                                                                                    data-cy="last-name"
                                                                                    string={
                                                                                        item.lastName
                                                                                    }
                                                                                ></TitleCase>
                                                                            </Grid>
                                                                            <Grid
                                                                                data-cy="saved-teacher-grades"
                                                                                item
                                                                                xs={
                                                                                    12
                                                                                }
                                                                                sx={{
                                                                                    marginTop:
                                                                                        '8px',
                                                                                }}
                                                                            >
                                                                                {item.grades.map(
                                                                                    grade => {
                                                                                        return (
                                                                                            <Typography>
                                                                                                {
                                                                                                    grades.find(
                                                                                                        g =>
                                                                                                            g.name ===
                                                                                                            grade
                                                                                                    )
                                                                                                        .title
                                                                                                }
                                                                                            </Typography>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </Grid>
                                                                        </Grid>
                                                                    </TeacherResultsCard>
                                                                );
                                                            }
                                                            return <div></div>;
                                                        }
                                                    )}
                                                </TeacherResultsContainerBox>
                                            ) : (
                                                <TeacherResultsContainerBox>
                                                    <Typography>
                                                        You do not have any
                                                        saved teachers
                                                    </Typography>
                                                </TeacherResultsContainerBox>
                                            )}
                                        </Grid>
                                    </Route>
                                </Grid>
                            </>
                            {/* ) : (
                                    <Box sx={{ padding: '44px' }}>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                marginBottom: '12px',
                                            }}
                                        >
                                            Payment Method Not Active
                                        </Typography>
                                        {schoolInfo.school.Data.planStatus ===
                                        'Pending' ? (
                                            <Typography variant="body">
                                                Subscription status is pending.
                                                Please wait a few minutes until
                                                your account is ready.
                                            </Typography>
                                        ) : (
                                            <Typography variant="body">
                                                Your school does not have an
                                                active payment method. Please
                                                subscribe to a plan to use the
                                                airTeachr service.
                                            </Typography>
                                        )}

                                        <Box>
                                            <BookingButton
                                                sx={{
                                                    maxWidth: '250px',
                                                    marginTop: '22px',
                                                }}
                                                variant="outlined"
                                                onClick={() => {
                                                    history.push(
                                                        '/profile/billing'
                                                    );
                                                }}
                                            >
                                                View payment method
                                            </BookingButton>
                                        </Box>
                                    </Box>
                                )} */}
                        </Box>
                        {/* // ) : (
                             <Box sx={{ padding: '44px' }}>
                                 <Typography
                                     variant="h4"
                                     sx={{
                                         marginBottom: '12px',
                                     }}
                                 >
                                     No Payment Method
                                 </Typography>

                                 <Typography variant="body">
                                     Your school has not subscribed to this
                                     service. You cannot create a booking until a
                                     payment method and a plan is selected.
                                 </Typography>
                        <Box>
                                     <BookingButton
                                         sx={{
                                             maxWidth: '250px',
                                             marginTop: '22px',
                                     }}
                                         variant="outlined"
                                         onClick={() => {
                                             history.push('/profile/billing');
                                         }}
                                     >
                                         Add payment method
                                     </BookingButton>
                                 </Box>
                             </Box>
                         )} */}
                    </Box>
                </>
            ) : (
                <Loading />
            )}
        </>
    );
};

export default withAuthenticationRequired(SavedTeachers, {
    onRedirecting: () => <Loading />,
});
